"use client";

import React from "react";
import { Brain, Leaf, Heart, Compass, ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const modalities = [{
  id: "item-1",
  title: "Medical vs. Dental",
  subtitle: "Understanding the distinction",
  icon: Brain,
  description: "Since sleep apnea is a diagnosed medical condition, treatment with an oral appliance is billed through your medical insurance rather than your dental plan. This distinction is crucial for maximizing your benefits and minimizing out-of-pocket expenses."
}, {
  id: "item-2",
  title: "The Approval Process",
  subtitle: "Pre-authorization requirements",
  icon: Leaf,
  description: "Most medical carriers require a 'Prior Authorization' before treatment begins. Our team handles the heavy lifting—coordinating with your primary physician and insurance provider to secure the necessary approvals and confirm your coverage levels."
}, {
  id: "item-3",
  title: "Your Financial Path",
  subtitle: "Deductibles and co-insurance",
  icon: Heart,
  description: "Your medical plan's annual deductible and co-insurance will apply to your therapy. We provide a transparent breakdown of these costs during your initial consultation, helping you understand exactly how your specific policy supports your treatment."
}, {
  id: "item-4",
  title: "Network Navigation",
  subtitle: "Maximizing your policy benefits",
  icon: Compass,
  description: "We work with a wide range of medical carriers. Even if we are considered out-of-network for your specific plan, we can often apply for 'Gap Exceptions' or 'Network Adequacy' waivers, allowing you to access treatment at in-network rates."
}];
export default function HowWeWorkTogether() {
  const {
    ref: leftRef,
    isVisible: leftIsVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: rightIsVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="98419" id="coverage-costs" className="relative py-24 lg:py-32 bg-background overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -right-[10%] w-[70%] h-[70%] rounded-full bg-primary/5 blur-3xl opacity-50" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[60%] h-[60%] rounded-full bg-secondary/30 blur-3xl opacity-50" />
      </div>

      <div className="container mx-auto px-6 lg:px-8 max-w-7xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24 items-start">
          
          {}
          <div ref={leftRef} className={`lg:col-span-5 lg:sticky lg:top-32 transition-all duration-1000 ease-out ${leftIsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              Insurance & Coverage
            </div>
            <h2 className="text-4xl md:text-5xl font-sans font-bold text-foreground mb-6 leading-tight tracking-tight">
              Medical coverage for your <span className="text-primary italic">dental sleep care.</span>
            </h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              Navigating insurance for sleep apnea treatment doesn't have to be overwhelming. Because sleep apnea is a medical disorder, we bill your medical insurance—not dental—ensuring your oral appliance therapy is recognized as a medical necessity.
            </p>
            
            <div className="relative rounded-[1rem] overflow-hidden aspect-[4/3] mb-8 shadow-xl shadow-shadow-color/5 border border-border">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1612128/img/therapy_session_soft_focus.png" alt="A professional consultation discussing medical insurance benefits" className="object-cover w-full h-full transition-transform duration-700 hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent" />
            </div>

            <Button asChild size="lg" className="rounded-lg group bg-primary hover:bg-primary/90 text-primary-foreground font-semibold">
              <Link to="/InsurancePage">
                Check Your Coverage
                <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-1000 delay-200 ease-out ${rightIsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <div className="bg-card/50 backdrop-blur-sm border border-border rounded-[1.5rem] p-6 md:p-10 shadow-lg shadow-shadow-color/5">
              <Accordion type="single" collapsible className="w-full" defaultValue="item-1">
                {modalities.map((modality, index) => {
                const Icon = modality.icon;
                return <AccordionItem key={modality.id} value={modality.id} data-index={index} className="border-b border-border/60 last:border-0 py-2">
                      <AccordionTrigger className="hover:no-underline group py-6">
                        <div className="flex items-center gap-6 text-left">
                          <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                            <Icon className="size-6" />
                          </div>
                          <div>
                            <h3 className="text-xl font-semibold text-foreground group-hover:text-primary transition-colors">
                              {modality.title}
                            </h3>
                            <p className="text-sm text-muted-foreground mt-1 font-sans">
                              {modality.subtitle}
                            </p>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="pl-[4.5rem] pr-4 pb-6 text-base text-muted-foreground leading-relaxed">
                          {modality.description}
                        </div>
                      </AccordionContent>
                    </AccordionItem>;
              })}
              </Accordion>
            </div>
          </div>

        </div>
      </div>
    </section>;
}